/**
 * ActiveModulesPanel - Panel informativo de los módulos activos de DamyxIA
 * Muestra descripción detallada de cada módulo y cómo afecta las respuestas
 */
import { useState } from 'react';
import { X, ChevronDown, ChevronUp, Sparkles, Brain, MessageSquare, Zap, Search, Code, BarChart2, Image, GitBranch, ShieldCheck, Database, Layers, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Información detallada de cada módulo
const MODULE_INFO = {
  es: {
    REP: {
      name: 'Resolución Estratégica de Problemas',
      shortName: 'REP',
      description: 'Analiza problemas complejos y proporciona soluciones estructuradas paso a paso.',
      howItHelps: 'Descompone el problema en partes manejables, identifica la causa raíz y genera un plan de acción claro.',
      categories: {
        'Técnico': 'Problemas de código, arquitectura o sistemas',
        'Económico': 'Análisis de costos, presupuestos o finanzas',
        'Organizacional': 'Estructura de equipos, procesos o flujos de trabajo',
        'Decisión': 'Evaluación de opciones y toma de decisiones',
        'Estratégico': 'Planificación a largo plazo y visión global',
        'Práctico': 'Soluciones aplicables e implementables',
        'General': 'Problemas diversos que requieren análisis'
      },
      icon: Brain,
      color: 'purple'
    },
    MCEP: {
      name: 'Comunicación Empática y Profesional',
      shortName: 'MCEP',
      description: 'Adapta el tono y estilo de comunicación según el contexto emocional.',
      howItHelps: 'Asegura respuestas claras, respetuosas y apropiadas para cada situación.',
      categories: {
        'Sensible': 'Temas delicados que requieren tacto',
        'Empático': 'Situaciones que necesitan comprensión emocional',
        'Clarificador': 'Explicaciones claras para confusiones',
        'Urgente': 'Respuestas rápidas y directas',
        'Complejo': 'Temas técnicos explicados con claridad',
        'Profesional': 'Comunicación formal y estructurada',
        'Estándar': 'Comunicación general equilibrada'
      },
      icon: MessageSquare,
      color: 'teal'
    },
    ANALYSIS: {
      name: 'Análisis Técnico',
      shortName: 'Análisis',
      description: 'Realiza análisis profundos de información técnica y datos.',
      howItHelps: 'Proporciona insights detallados, comparaciones y evaluaciones estructuradas.',
      categories: {
        'Technical': 'Análisis de sistemas y arquitectura',
        'Comparative': 'Comparación entre opciones o tecnologías',
        'Structural': 'Evaluación de estructura y organización',
        'Deep': 'Análisis profundo y exhaustivo'
      },
      categoryLabels: {
        'Technical': 'Técnico',
        'Comparative': 'Comparativo',
        'Structural': 'Estructural',
        'Deep': 'Profundo'
      },
      icon: BarChart2,
      color: 'blue'
    },
    CODE: {
      name: 'Generación de Código',
      shortName: 'Código',
      description: 'Genera código funcional, optimizado y bien documentado.',
      howItHelps: 'Crea soluciones de código siguiendo mejores prácticas y patrones modernos.',
      categories: {
        'Código': 'Generación de código fuente'
      },
      icon: Code,
      color: 'emerald'
    },
    CHART: {
      name: 'Visualización de Datos',
      shortName: 'Gráficos',
      description: 'Genera gráficos y visualizaciones para representar datos.',
      howItHelps: 'Transforma datos en visualizaciones comprensibles e interactivas.',
      categories: {
        'Gráfico': 'Visualización de datos'
      },
      icon: BarChart2,
      color: 'amber'
    },
    DIAGRAM: {
      name: 'Diagramación',
      shortName: 'Diagramas',
      description: 'Crea diagramas técnicos y de flujo para visualizar procesos.',
      howItHelps: 'Representa visualmente arquitecturas, flujos y relaciones.',
      categories: {
        'Diagrama': 'Diagramas técnicos y de flujo'
      },
      icon: GitBranch,
      color: 'cyan'
    },
    IMAGE: {
      name: 'Generación de Imágenes',
      shortName: 'Imágenes',
      description: 'Genera imágenes con inteligencia artificial basadas en descripciones.',
      howItHelps: 'Crea visuales únicos y personalizados según tus necesidades.',
      categories: {
        'Imagen': 'Generación de imágenes con IA'
      },
      icon: Image,
      color: 'pink'
    },
    SEARCH: {
      name: 'Búsqueda Web',
      shortName: 'Búsqueda',
      description: 'Busca información actualizada en internet.',
      howItHelps: 'Proporciona datos recientes, noticias y información en tiempo real.',
      categories: {
        'Web': 'Búsqueda en internet'
      },
      icon: Search,
      color: 'orange'
    },
    VAI: {
      name: 'Verificación y Autocorrección',
      shortName: 'VAI',
      description: 'Revisa y corrige respuestas antes de enviarlas para garantizar calidad.',
      howItHelps: 'Detecta errores lógicos, contradicciones, mejora claridad y verifica coherencia con otros módulos.',
      categories: {
        'Verificado': 'Respuesta verificada sin problemas',
        'Analizado': 'Análisis de calidad completado',
        'Activo': 'Verificación en proceso',
        '1 ajustes': 'Un ajuste aplicado',
        '2 ajustes': 'Dos ajustes aplicados',
        '3 ajustes': 'Tres ajustes aplicados'
      },
      icon: ShieldCheck,
      color: 'indigo'
    },
    MCC: {
      name: 'Memoria Controlada',
      shortName: 'MCC',
      description: 'Gestiona la memoria de la conversación de forma controlada por el usuario.',
      howItHelps: 'Recuerda información solo cuando lo pedís explícitamente. Mantiene coherencia sin guardar datos innecesarios.',
      categories: {
        'Guardando': 'Guardando información en memoria',
        'Olvidando': 'Eliminando información de memoria',
        'Recordando': 'Recuperando información guardada',
        'Activo': 'Módulo de memoria activo'
      },
      icon: Database,
      color: 'cyan'
    },
    RMC: {
      name: 'Razonamiento Multicapa',
      shortName: 'RMC',
      description: 'Analiza problemas desde múltiples perspectivas simultáneamente para generar respuestas profundas y bien justificadas.',
      howItHelps: 'Piensa en capas: lógica, contextual, técnica y estratégica. Combina las mejores conclusiones de cada perspectiva.',
      categories: {
        'Lógico': 'Análisis de premisas, coherencia y conclusiones',
        'Contextual': 'Consideración del historial y contexto del usuario',
        'Técnico': 'Evaluación de aspectos técnicos y comparaciones',
        'Estratégico': 'Proyección de escenarios y recomendaciones',
        'Análisis': 'Análisis profundo del problema',
        'Comparación': 'Comparativa entre opciones',
        'Decisión': 'Evaluación para toma de decisiones',
        'Exploración': 'Exploración de conceptos',
        'Diagnóstico': 'Resolución de problemas',
        'Planificación': 'Planificación estratégica',
        'Conceptual': 'Entendimiento de conceptos',
        'Procedimiento': 'Guía paso a paso'
      },
      icon: Layers,
      color: 'violet'
    },
    AEAE: {
      name: 'Análisis Emocional y Adaptación Empática',
      shortName: 'AEAE',
      description: 'Detecta el estado emocional del usuario y adapta el tono de respuesta manteniendo profesionalismo.',
      howItHelps: 'Identifica frustración, entusiasmo, duda o satisfacción, y ajusta la comunicación para ser más efectiva sin dramatizar.',
      categories: {
        'Frustración': 'Respuestas directas y orientadas a solución',
        'Entusiasmo': 'Información enriquecida y motivadora',
        'Duda': 'Explicaciones balanceadas y opciones claras',
        'Satisfacción': 'Reconocimiento breve y siguientes pasos',
        'Neutral': 'Comunicación estándar profesional'
      },
      icon: MessageSquare,
      color: 'rose'
    },
    ORACLE: {
      name: 'Oráculo Externo',
      shortName: 'Oracle',
      description: 'Consulta fuentes externas para obtener información actualizada y factual cuando DamyxIA detecta que lo necesita.',
      howItHelps: 'Proporciona datos precisos sobre eventos actuales, cargos políticos, precios de criptomonedas, clima y tipos de cambio en tiempo real.',
      categories: {
        'current_events': 'Noticias y eventos recientes',
        'current_positions': 'Cargos y posiciones actuales',
        'real_time_data': 'Datos en tiempo real',
        'recent_changes': 'Cambios recientes',
        'factual_verification': 'Verificación de datos',
        'crypto': '₿ Precios de criptomonedas (CoinGecko)',
        'weather': '🌤️ Clima en tiempo real (Open-Meteo)',
        'news': '📰 Titulares de noticias',
        'finance': '💹 Tipos de cambio (ExchangeRate-API)',
        'gpt': '🌐 Consulta GPT-5.2'
      },
      icon: Globe,
      color: 'sky'
    }
  },
  en: {
    REP: {
      name: 'Strategic Problem Resolution',
      shortName: 'REP',
      description: 'Analyzes complex problems and provides step-by-step structured solutions.',
      howItHelps: 'Breaks down problems into manageable parts, identifies root causes, and generates a clear action plan.',
      categories: {
        'Técnico': 'Code, architecture, or system issues',
        'Económico': 'Cost analysis, budgets, or finances',
        'Organizacional': 'Team structure, processes, or workflows',
        'Decisión': 'Option evaluation and decision making',
        'Estratégico': 'Long-term planning and global vision',
        'Práctico': 'Applicable and implementable solutions',
        'General': 'Various problems requiring analysis'
      },
      categoryLabels: {
        'Técnico': 'Technical',
        'Económico': 'Economic',
        'Organizacional': 'Organizational',
        'Decisión': 'Decision',
        'Estratégico': 'Strategic',
        'Práctico': 'Practical',
        'General': 'General'
      },
      icon: Brain,
      color: 'purple'
    },
    MCEP: {
      name: 'Empathetic Professional Communication',
      shortName: 'MCEP',
      description: 'Adapts tone and communication style based on emotional context.',
      howItHelps: 'Ensures clear, respectful, and appropriate responses for each situation.',
      categories: {
        'Sensible': 'Sensitive topics requiring tact',
        'Empático': 'Situations needing emotional understanding',
        'Clarificador': 'Clear explanations for confusion',
        'Urgente': 'Quick and direct responses',
        'Complejo': 'Technical topics explained clearly',
        'Profesional': 'Formal and structured communication',
        'Estándar': 'Balanced general communication'
      },
      icon: MessageSquare,
      color: 'teal'
    },
    ANALYSIS: {
      name: 'Technical Analysis',
      shortName: 'Analysis',
      description: 'Performs deep analysis of technical information and data.',
      howItHelps: 'Provides detailed insights, comparisons, and structured evaluations.',
      categories: {
        'Technical': 'System and architecture analysis',
        'Comparative': 'Comparison between options or technologies',
        'Structural': 'Structure and organization evaluation',
        'Deep': 'Deep and exhaustive analysis'
      },
      icon: BarChart2,
      color: 'blue'
    },
    CODE: {
      name: 'Code Generation',
      shortName: 'Code',
      description: 'Generates functional, optimized, and well-documented code.',
      howItHelps: 'Creates code solutions following best practices and modern patterns.',
      categories: {
        'Código': 'Source code generation'
      },
      icon: Code,
      color: 'emerald'
    },
    CHART: {
      name: 'Data Visualization',
      shortName: 'Charts',
      description: 'Generates charts and visualizations to represent data.',
      howItHelps: 'Transforms data into understandable and interactive visualizations.',
      categories: {
        'Gráfico': 'Data visualization'
      },
      icon: BarChart2,
      color: 'amber'
    },
    DIAGRAM: {
      name: 'Diagramming',
      shortName: 'Diagrams',
      description: 'Creates technical and flow diagrams to visualize processes.',
      howItHelps: 'Visually represents architectures, flows, and relationships.',
      categories: {
        'Diagrama': 'Technical and flow diagrams'
      },
      icon: GitBranch,
      color: 'cyan'
    },
    IMAGE: {
      name: 'Image Generation',
      shortName: 'Images',
      description: 'Generates AI images based on descriptions.',
      howItHelps: 'Creates unique and personalized visuals according to your needs.',
      categories: {
        'Imagen': 'AI image generation'
      },
      icon: Image,
      color: 'pink'
    },
    SEARCH: {
      name: 'Web Search',
      shortName: 'Search',
      description: 'Searches for updated information on the internet.',
      howItHelps: 'Provides recent data, news, and real-time information.',
      categories: {
        'Web': 'Internet search'
      },
      icon: Search,
      color: 'orange'
    },
    VAI: {
      name: 'Verification & Auto-correction',
      shortName: 'VAI',
      description: 'Reviews and corrects responses before sending to ensure quality.',
      howItHelps: 'Detects logical errors, contradictions, improves clarity and verifies coherence with other modules.',
      categories: {
        'Verificado': 'Verified response without issues',
        'Analizado': 'Quality analysis completed',
        'Activo': 'Verification in progress',
        '1 ajustes': 'One adjustment applied',
        '2 ajustes': 'Two adjustments applied',
        '3 ajustes': 'Three adjustments applied'
      },
      icon: ShieldCheck,
      color: 'indigo'
    },
    MCC: {
      name: 'Controlled Memory',
      shortName: 'MCC',
      description: 'Manages conversation memory in a user-controlled way.',
      howItHelps: 'Remembers information only when you explicitly ask. Maintains coherence without saving unnecessary data.',
      categories: {
        'Guardando': 'Saving information to memory',
        'Olvidando': 'Removing information from memory',
        'Recordando': 'Retrieving saved information',
        'Activo': 'Memory module active'
      },
      icon: Database,
      color: 'cyan'
    },
    RMC: {
      name: 'Multi-Layer Reasoning',
      shortName: 'RMC',
      description: 'Analyzes problems from multiple perspectives simultaneously to generate deep and well-justified responses.',
      howItHelps: 'Thinks in layers: logical, contextual, technical, and strategic. Combines the best conclusions from each perspective.',
      categories: {
        'Lógico': 'Analysis of premises, coherence and conclusions',
        'Contextual': 'Consideration of history and user context',
        'Técnico': 'Evaluation of technical aspects and comparisons',
        'Estratégico': 'Scenario projection and recommendations',
        'Análisis': 'Deep problem analysis',
        'Comparación': 'Comparison between options',
        'Decisión': 'Decision-making evaluation',
        'Exploración': 'Concept exploration',
        'Diagnóstico': 'Problem troubleshooting',
        'Planificación': 'Strategic planning',
        'Conceptual': 'Understanding concepts',
        'Procedimiento': 'Step-by-step guide'
      },
      icon: Layers,
      color: 'violet'
    },
    AEAE: {
      name: 'Emotional Analysis & Empathetic Adaptation',
      shortName: 'AEAE',
      description: 'Detects user emotional state and adapts response tone while maintaining professionalism.',
      howItHelps: 'Identifies frustration, enthusiasm, doubt, or satisfaction, and adjusts communication to be more effective without dramatizing.',
      categories: {
        'Frustración': 'Direct and solution-oriented responses',
        'Entusiasmo': 'Enriched and motivating information',
        'Duda': 'Balanced explanations and clear options',
        'Satisfacción': 'Brief acknowledgment and next steps',
        'Neutral': 'Standard professional communication'
      },
      icon: MessageSquare,
      color: 'rose'
    },
    ORACLE: {
      name: 'External Oracle',
      shortName: 'Oracle',
      description: 'Queries external sources to obtain updated and factual information when DamyxIA detects it is needed.',
      howItHelps: 'Provides accurate data about current events, political positions, cryptocurrency prices, weather and exchange rates in real-time.',
      categories: {
        'current_events': 'News and recent events',
        'current_positions': 'Current positions and roles',
        'real_time_data': 'Real-time data',
        'recent_changes': 'Recent changes',
        'factual_verification': 'Data verification',
        'crypto': '₿ Cryptocurrency prices (CoinGecko)',
        'weather': '🌤️ Real-time weather (Open-Meteo)',
        'news': '📰 News headlines',
        'finance': '💹 Exchange rates (ExchangeRate-API)',
        'gpt': '🌐 GPT-5.2 query'
      },
      icon: Globe,
      color: 'sky'
    }
  }
};

// Colores por módulo
const MODULE_COLORS = {
  purple: {
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
    iconBg: 'bg-purple-500/20',
    hover: 'hover:bg-purple-500/20'
  },
  teal: {
    bg: 'bg-teal-500/10',
    border: 'border-teal-500/30',
    text: 'text-teal-400',
    iconBg: 'bg-teal-500/20',
    hover: 'hover:bg-teal-500/20'
  },
  blue: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    iconBg: 'bg-blue-500/20',
    hover: 'hover:bg-blue-500/20'
  },
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    iconBg: 'bg-emerald-500/20',
    hover: 'hover:bg-emerald-500/20'
  },
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    iconBg: 'bg-amber-500/20',
    hover: 'hover:bg-amber-500/20'
  },
  cyan: {
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    iconBg: 'bg-cyan-500/20',
    hover: 'hover:bg-cyan-500/20'
  },
  pink: {
    bg: 'bg-pink-500/10',
    border: 'border-pink-500/30',
    text: 'text-pink-400',
    iconBg: 'bg-pink-500/20',
    hover: 'hover:bg-pink-500/20'
  },
  orange: {
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
    iconBg: 'bg-orange-500/20',
    hover: 'hover:bg-orange-500/20'
  },
  indigo: {
    bg: 'bg-indigo-500/10',
    border: 'border-indigo-500/30',
    text: 'text-indigo-400',
    iconBg: 'bg-indigo-500/20',
    hover: 'hover:bg-indigo-500/20'
  },
  violet: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    text: 'text-violet-400',
    iconBg: 'bg-violet-500/20',
    hover: 'hover:bg-violet-500/20'
  },
  rose: {
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    text: 'text-rose-400',
    iconBg: 'bg-rose-500/20',
    hover: 'hover:bg-rose-500/20'
  },
  sky: {
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
    text: 'text-sky-400',
    iconBg: 'bg-sky-500/20',
    hover: 'hover:bg-sky-500/20'
  }
};

// Componente para cada módulo expandible
const ModuleCard = ({ module, moduleInfo, colors, isExpanded, onToggle, language }) => {
  const Icon = moduleInfo.icon;
  const descLabel = language === 'en' ? 'Description' : 'Descripción';
  const howLabel = language === 'en' ? 'How it helps' : 'Cómo ayuda';
  
  return (
    <div 
      className={`rounded-xl border transition-all duration-200 ${colors.bg} ${colors.border}`}
      data-testid={`module-card-${module.module.toLowerCase()}`}
    >
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${colors.hover}`}
        data-testid={`module-card-toggle-${module.module.toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors.iconBg}`}>
            <Icon className={`w-4 h-4 ${colors.text}`} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${colors.text}`}>
                {moduleInfo.name}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}>
                {moduleInfo.categoryLabels?.[module.category] || module.category}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">
              {module.icon} {module.name}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className={`w-4 h-4 ${colors.text}`} />
        ) : (
          <ChevronDown className={`w-4 h-4 ${colors.text}`} />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-800/50 pt-3">
          <div>
            <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-1">
              {descLabel}
            </h4>
            <p className="text-sm text-zinc-300">
              {moduleInfo.description}
            </p>
          </div>
          <div>
            <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-1">
              {howLabel}
            </h4>
            <p className="text-sm text-zinc-300">
              {moduleInfo.howItHelps}
            </p>
          </div>
          {moduleInfo.categories[module.category] && (
            <div className={`p-2 rounded-lg ${colors.bg} border ${colors.border}`}>
              <p className={`text-xs ${colors.text}`}>
                <span className="font-medium">{moduleInfo.categoryLabels?.[module.category] || module.category}:</span>{' '}
                {moduleInfo.categories[module.category]}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ActiveModulesPanel = ({ modules, isOpen, onClose }) => {
  const { language } = useLanguage();
  const [expandedModules, setExpandedModules] = useState({});
  
  const txt = {
    es: {
      title: 'Módulos de DamyxIA',
      subtitle: 'Módulos activos en esta respuesta',
      noModules: 'No hay módulos activos',
      noModulesDesc: 'Los módulos se activan automáticamente según el tipo de consulta.',
      howItWorks: 'Cómo funciona',
      howItWorksDesc: 'DamyxIA analiza tu mensaje y activa los módulos más adecuados para darte la mejor respuesta posible.',
      close: 'Cerrar'
    },
    en: {
      title: 'DamyxIA Modules',
      subtitle: 'Active modules in this response',
      noModules: 'No active modules',
      noModulesDesc: 'Modules are automatically activated based on the type of query.',
      howItWorks: 'How it works',
      howItWorksDesc: 'DamyxIA analyzes your message and activates the most suitable modules to give you the best possible response.',
      close: 'Close'
    }
  };
  const t = txt[language] || txt.es;
  const moduleInfoLang = MODULE_INFO[language] || MODULE_INFO.es;
  
  if (!isOpen) return null;
  
  const toggleModule = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="active-modules-panel-overlay"
    >
      <div 
        className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden"
        data-testid="active-modules-panel"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 bg-gradient-to-r from-indigo-900/30 to-purple-900/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-xl">
                <Sparkles className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{t.title}</h2>
                <p className="text-xs text-zinc-400">{t.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              data-testid="close-modules-panel-btn"
            >
              <X className="w-5 h-5 text-zinc-400 hover:text-white" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-5 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {modules && modules.length > 0 ? (
            <div className="space-y-3">
              {modules.map((module, idx) => {
                const moduleInfo = moduleInfoLang[module.module];
                if (!moduleInfo) return null;
                
                const colors = MODULE_COLORS[moduleInfo.color] || MODULE_COLORS.blue;
                const moduleKey = `${module.module}-${idx}`;
                
                return (
                  <ModuleCard
                    key={moduleKey}
                    module={module}
                    moduleInfo={moduleInfo}
                    colors={colors}
                    isExpanded={expandedModules[moduleKey]}
                    onToggle={() => toggleModule(moduleKey)}
                    language={language}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-zinc-800 rounded-full flex items-center justify-center">
                <Zap className="w-8 h-8 text-zinc-600" />
              </div>
              <h3 className="text-lg font-medium text-zinc-300 mb-2">{t.noModules}</h3>
              <p className="text-sm text-zinc-500 max-w-sm mx-auto">{t.noModulesDesc}</p>
            </div>
          )}
          
          {/* How it works section */}
          <div className="mt-6 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-indigo-400" />
              <h3 className="text-sm font-medium text-zinc-300">{t.howItWorks}</h3>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              {t.howItWorksDesc}
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-900/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
            data-testid="close-modules-panel-btn-footer"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveModulesPanel;
